/**
 * Aurora — 设置面板
 * MacOS27 风格：账户信息 / 主题切换 / 语言 / 模型额度 / 退出
 */
import { useState, useEffect } from 'react';
import type { FC } from 'react';
import { AuroraLogo } from './Icons';
import './SettingsPanel.css';

export type Theme = 'light' | 'dark' | 'system';

interface UserInfo {
  id: string;
  email: string;
  name: string;
  role: string;
  plan?: string;
  points?: number;
  selectedModels?: string[];
}

interface Props {
  user: UserInfo | null;
  theme: Theme;
  language: 'zh' | 'en';
  onThemeChange: (t: Theme) => void;
  onLanguageChange: (l: 'zh' | 'en') => void;
  onLogout: () => void;
  onClose: () => void;
}

const THEME_OPTIONS: { value: Theme; label: string; icon: string }[] = [
  { value: 'light', label: '浅色', icon: '☀️' },
  { value: 'dark',  label: '深色', icon: '🌙' },
  { value: 'system',label: '跟随系统', icon: '⚙️' },
];

const LANG_OPTIONS = [
  { value: 'zh' as const, label: '简体中文', native: '简体中文' },
  { value: 'en' as const, label: 'English',   native: 'English' },
];

const SettingsPanel: FC<Props> = ({
  user, theme, language, onThemeChange, onLanguageChange, onLogout, onClose,
}) => {
  const [activeSection, setActiveSection] = useState<'account' | 'appearance' | 'models' | 'about'>('account');
  const [points, setPoints] = useState<number | null>(user?.points ?? null);

  useEffect(() => {
    const token = localStorage.getItem('aurora_token');
    if (!token || !user) return;
    fetch('/api/points/balance', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (d.success) setPoints(d.data.balance); })
      .catch(() => {});
  }, [user]);

  const sections = [
    { id: 'account',    label: '账号',   icon: '👤' },
    { id: 'appearance', label: '外观',   icon: '🎨' },
    { id: 'models',     label: '模型',   icon: '🤖' },
    { id: 'about',      label: '关于',   icon: 'ℹ️' },
  ] as const;

  return (
    <div className="sp__overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sp__panel">
        {/* 顶部 */}
        <div className="sp__topbar">
          <span className="sp__title">设置</span>
          <button className="sp__close" onClick={onClose}>✕</button>
        </div>

        <div className="sp__body">
          {/* 左侧导航 */}
          <nav className="sp__nav">
            {sections.map((s) => (
              <button
                key={s.id}
                className={`sp__nav-item ${activeSection === s.id ? 'sp__nav-item--active' : ''}`}
                onClick={() => setActiveSection(s.id)}
              >
                <span className="sp__nav-icon">{s.icon}</span>
                <span>{s.label}</span>
              </button>
            ))}
            <div className="sp__nav-spacer" />
            <button className="sp__nav-item sp__nav-item--danger" onClick={onLogout}>
              <span className="sp__nav-icon">🚪</span>
              <span>退出登录</span>
            </button>
          </nav>

          {/* 右侧内容 */}
          <div className="sp__content">

            {/* ── 账号 ── */}
            {activeSection === 'account' && (
              <div className="sp__section">
                <h2 className="sp__section-title">账号信息</h2>

                {/* 用户卡片 */}
                <div className="sp__user-card">
                  <div className="sp__avatar">
                    <span>{(user?.name || 'U')[0].toUpperCase()}</span>
                    <AuroraLogo size={12} className="sp__avatar-badge" />
                  </div>
                  <div className="sp__user-info">
                    <div className="sp__user-name">{user?.name || '未登录'}</div>
                    <div className="sp__user-email">{user?.email || ''}</div>
                    <div className="sp__user-role">
                      {user?.role === 'admin' ? '管理员' : user?.plan === 'pro' ? 'Pro 会员' : '免费用户'}
                    </div>
                  </div>
                </div>

                {/* 积分 */}
                <div className="sp__group">
                  <div className="sp__group-title">积分余额</div>
                  <div className="sp__points-card">
                    <div className="sp__points-main">
                      <span className="sp__points-icon">💰</span>
                      <span className="sp__points-amount">
                        {points !== null ? points.toLocaleString() : '—'}
                      </span>
                      <span className="sp__points-unit">积分</span>
                    </div>
                    <div className="sp__points-sub">
                      1 积分 ≈ 0.01 元 · 注册赠 10,000 积分
                    </div>
                  </div>
                </div>

                {/* 已选模型 */}
                <div className="sp__group">
                  <div className="sp__group-title">已启用模型</div>
                  <div className="sp__stat-row">
                    <span className="sp__stat-val">{user?.selectedModels?.length ?? 0}</span>
                    <span className="sp__stat-label">个模型已选择</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── 外观 ── */}
            {activeSection === 'appearance' && (
              <div className="sp__section">
                <h2 className="sp__section-title">外观设置</h2>

                <div className="sp__group">
                  <div className="sp__group-title">主题</div>
                  <div className="sp__theme-grid">
                    {THEME_OPTIONS.map((t) => (
                      <button
                        key={t.value}
                        className={`sp__theme-btn ${theme === t.value ? 'sp__theme-btn--active' : ''}`}
                        onClick={() => onThemeChange(t.value)}
                      >
                        <span className="sp__theme-icon">{t.icon}</span>
                        <span className="sp__theme-label">{t.label}</span>
                        {theme === t.value && <span className="sp__theme-check">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="sp__group">
                  <div className="sp__group-title">语言 / Language</div>
                  <div className="sp__lang-grid">
                    {LANG_OPTIONS.map((l) => (
                      <button
                        key={l.value}
                        className={`sp__lang-btn ${language === l.value ? 'sp__lang-btn--active' : ''}`}
                        onClick={() => onLanguageChange(l.value)}
                      >
                        <span>{l.native}</span>
                        {language === l.value && <span className="sp__theme-check">✓</span>}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── 模型额度 ── */}
            {activeSection === 'models' && (
              <div className="sp__section">
                <h2 className="sp__section-title">模型额度</h2>
                <p className="sp__desc">
                  积分制度：每次 AI 调用消耗对应积分，积分不足时无法继续调用。
                </p>

                <div className="sp__group">
                  <div className="sp__group-title">消费说明</div>
                  <div className="sp__quota-list">
                    <div className="sp__quota-item">
                      <span className="sp__quota-icon">💬</span>
                      <span className="sp__quota-label">文本对话</span>
                      <span className="sp__quota-val">按 Token 计费，0.05~27 积分/1K Token</span>
                    </div>
                    <div className="sp__quota-item">
                      <span className="sp__quota-icon">🎨</span>
                      <span className="sp__quota-label">图片生成</span>
                      <span className="sp__quota-val">4~30 积分/张</span>
                    </div>
                    <div className="sp__quota-item">
                      <span className="sp__quota-icon">🎬</span>
                      <span className="sp__quota-label">视频生成</span>
                      <span className="sp__quota-val">5~100 积分/秒</span>
                    </div>
                    <div className="sp__quota-item">
                      <span className="sp__quota-icon">🎙️</span>
                      <span className="sp__quota-label">语音合成</span>
                      <span className="sp__quota-val">60 积分/千字符</span>
                    </div>
                    <div className="sp__quota-item">
                      <span className="sp__quota-icon">💻</span>
                      <span className="sp__quota-label">本地 CLI 模型</span>
                      <span className="sp__quota-val">订阅制 · 无额度限制</span>
                    </div>
                  </div>
                </div>

                <div className="sp__group">
                  <div className="sp__group-title">当前余额</div>
                  <div className="sp__points-card">
                    <div className="sp__points-main">
                      <span className="sp__points-icon">💰</span>
                      <span className="sp__points-amount">
                        {points !== null ? points.toLocaleString() : '—'}
                      </span>
                      <span className="sp__points-unit">积分</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── 关于 ── */}
            {activeSection === 'about' && (
              <div className="sp__section">
                <h2 className="sp__section-title">关于 Aurora</h2>
                <div className="sp__about-logo">
                  <AuroraLogo size={56} />
                </div>
                <div className="sp__about-name">Aurora IDE</div>
                <div className="sp__about-ver">版本 1.0.0 · BorealOS</div>
                <div className="sp__about-desc">
                  Aurora 是一款极光主题 AI 工作站，集成多模型对话、图片/视频/语音生成、
                  数字人合成等能力，由 BorealOS 团队倾力打造。
                </div>
                <div className="sp__about-links">
                  <a href="https://borealos-ide.pages.dev" target="_blank" rel="noopener noreferrer" className="sp__about-link">
                    🌐 官方网站
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
